/**
 * Speech-to-text mic buttons for Single Line Text / Multi Line Text / HTML Editor fields.
 *
 * Reuses the same recording + transcription flow as the /chat assistant (resources/js/chat.js):
 * record with MediaRecorder, render a live waveform, send the audio to /ai/api/transcribe
 * (AiProxyController), and insert the returned text into the field.
 *
 * Fields are targeted generically via a `data-field-type="text|multiline|html"` attribute that's
 * rendered on the input/textarea across the various create/edit/inline-edit blade templates, so
 * this one module covers the full-page forms, the create/edit dialogs, the mobile form, and the
 * shared browse-table / relation-table / section-table inline-edit widgets (edit_field.blade.php
 * and the #text-modal / #html-modal popups) without needing per-view wiring.
 */
(function () {
    if (!navigator.mediaDevices || !window.MediaRecorder) {
        return;
    }

    const SELECTOR = 'input[data-field-type="text"]:not([type="checkbox"]), textarea[data-field-type="multiline"], ' +
        'input[data-field-type="multiline"]:not([type="checkbox"]), textarea[data-field-type="html"]';

    const attached = typeof WeakSet !== 'undefined' ? new WeakSet() : null;

    // Fields with an in-flight recording/transcription, keyed by their source element, so a
    // closing popup (modal dismissed without saving, inline-edit cancelled) can tear them down
    // instead of leaving an orphaned overlay behind.
    const activeRecordings = new Map();

    function csrfToken() {
        return $('meta[name="csrf-token"]').attr('content');
    }

    // Same tenant/user meta tags chat.js reads — sent so the AI backend can attribute usage to
    // this tenant and apply this user's own permission scope, instead of an anonymous one.
    function getStoredTenantDb() {
        const meta = document.querySelector('meta[name="tenant"]');
        return meta ? meta.content : null;
    }

    function getCurrentUserId() {
        const meta = document.querySelector('meta[name="user_id"]');
        return meta ? meta.content : null;
    }

    function t(key, fallback) {
        return (window.translation && window.translation[key]) ? window.translation[key] : fallback;
    }

    function blobToBase64(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    function insertIntoInput(el, text) {
        const start = typeof el.selectionStart === 'number' ? el.selectionStart : el.value.length;
        const end = typeof el.selectionEnd === 'number' ? el.selectionEnd : el.value.length;
        const before = el.value.substring(0, start);
        const after = el.value.substring(end);
        const sep = before.length && !/\s$/.test(before) ? ' ' : '';

        el.value = before + sep + text + after;
        el.focus();
        const caret = (before + sep + text).length;
        if (el.setSelectionRange) {
            el.setSelectionRange(caret, caret);
        }
        $(el).trigger('input').trigger('keyup').trigger('change');
    }

    function insertIntoTinyMCE(editorId, text) {
        const editor = window.tinymce && window.tinymce.get(editorId);
        if (!editor) {
            return false;
        }
        editor.execCommand('mceInsertContent', false, text.replace(/\n/g, '<br>'));
        editor.fire('change');
        return true;
    }

    function insertText(el, fieldType, text) {
        if (fieldType === 'html' && insertIntoTinyMCE(el.id, text)) {
            return;
        }
        insertIntoInput(el, text);
    }

    // The element that actually occupies visible space for a field — for an HTML field once
    // TinyMCE has taken over, that's its rendered container, not the (now hidden) source textarea.
    function visibleTarget(el, fieldType) {
        if (fieldType === 'html') {
            // TinyMCE 5 (silver theme) renders its editor chrome as '.tox-tinymce'.
            const $mce = $(el).closest('.form-group, .edit-value-holder, .modal-body').find('.tox-tinymce').first();
            if ($mce.length) {
                return $mce[0];
            }
        }
        return el;
    }

    function buildOverlay() {
        const $overlay = $('<div class="speech-voice-bar"></div>');
        const $canvasWrap = $('<div class="speech-canvas-wrap"></div>');
        const $canvas = $('<canvas class="speech-canvas"></canvas>');
        const $processing = $('<div class="speech-processing-row"></div>').text(t('transcribing', 'Transcribing…'));

        const $cancel = $('<button type="button" class="speech-action-btn speech-cancel-btn"></button>')
            .attr('title', t('cancel', 'Cancel'))
            .html('<svg width="12" height="12" viewBox="0 0 13 13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="1.5" y1="1.5" x2="11.5" y2="11.5"/><line x1="11.5" y1="1.5" x2="1.5" y2="11.5"/></svg>');

        const $confirm = $('<button type="button" class="speech-action-btn speech-confirm-btn"></button>')
            .attr('title', t('done', 'Done'))
            .html('<svg width="13" height="10" viewBox="0 0 14 11" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1,5.5 5,9.5 13,1.5"/></svg>');

        $canvasWrap.append($canvas);
        $overlay.append($canvasWrap, $processing, $cancel, $confirm);

        return {$overlay, $canvas, $cancel, $confirm};
    }

    // Positioned in viewport coordinates and appended straight to <body> — deliberately not
    // relying on offsetParent()/position(), which get unreliable once the target sits behind
    // TinyMCE's rendered container (especially inside a Bootstrap modal).
    function positionOverlay($overlay, target) {
        const rect = target.getBoundingClientRect();

        $overlay.css({
            position: 'fixed',
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            zIndex: 10050
        });

        if ($overlay.parent()[0] !== document.body) {
            $('body').append($overlay);
        }
    }

    function startWaveform(canvas, analyser, stateRef) {
        const ctx = canvas.getContext('2d'), DPR = window.devicePixelRatio || 1;

        function draw() {
            if (!stateRef.recording) return;

            const W = canvas.offsetWidth, H = canvas.offsetHeight;
            if (canvas.width !== W * DPR || canvas.height !== H * DPR) {
                canvas.width = W * DPR;
                canvas.height = H * DPR;
                ctx.scale(DPR, DPR);
            }
            ctx.clearRect(0, 0, W, H);

            if (analyser) {
                const bins = analyser.frequencyBinCount, freqData = new Uint8Array(bins);
                analyser.getByteFrequencyData(freqData);
                const barW = 3, gap = 2.5, total = barW + gap, count = Math.floor(W / total),
                    step = Math.max(1, Math.floor(bins / count)), centerY = H / 2;
                ctx.fillStyle = '#94a3b8';
                for (let i = 0; i < count; i++) {
                    const val = freqData[i * step] / 255, half = Math.max(2, val * (H / 2) * .9),
                        x = i * total, r = Math.min(barW / 2, half);
                    ctx.beginPath();
                    ctx.roundRect(x, centerY - half, barW, half * 2, r);
                    ctx.fill();
                }
            }

            stateRef.rafId = requestAnimationFrame(draw);
        }

        stateRef.rafId = requestAnimationFrame(draw);
    }

    function stopWaveform(canvas, stateRef) {
        if (stateRef.rafId) {
            cancelAnimationFrame(stateRef.rafId);
            stateRef.rafId = null;
        }
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    function attachMic(el) {
        const fieldType = $(el).attr('data-field-type');

        // A handful of fields (the shared quick-add html/text popups) are reused across
        // multiple underlying fields, some with speech-to-text enabled and some without, so
        // re-scanning them toggles the existing mic button's visibility instead of re-creating it.
        if (attached && attached.has(el)) {
            $(el).parent().find('> .speech-mic-btn').toggle(!!fieldType);
            return;
        }

        if (!fieldType) {
            return;
        }
        if (attached) attached.add(el);

        const $wrap = $(el).parent();
        if (!$wrap.length) {
            return;
        }
        if ($wrap.css('position') === 'static') {
            $wrap.css('position', 'relative');
        }

        const $btn = $('<button type="button" class="speech-mic-btn" tabindex="-1"></button>')
            .attr('title', t('speech_to_text', 'Speech to text'))
            .append('<i class="fas fa-microphone"></i>');
        $wrap.append($btn);

        let stream = null, mediaRecorder = null, chunks = [], mimeType = null;
        let audioCtx = null, analyser = null;
        const waveState = {recording: false, rafId: null};
        let $target = null, $overlay = null, discarded = false;

        function reset() {
            activeRecordings.delete(el);
            waveState.recording = false;
            if (audioCtx) {
                audioCtx.close().catch(() => {});
                audioCtx = null;
            }
            analyser = null;
            if (stream) {
                stream.getTracks().forEach(tr => tr.stop());
                stream = null;
            }
            if ($overlay) {
                $overlay.remove();
                $overlay = null;
            }
            if ($target) {
                $target.css('visibility', '');
                $target = null;
            }
            $btn.css('display', '');
        }

        // Called when the field's popup goes away out from under an active recording (modal
        // dismissed without saving, inline-edit row cancelled) — discard, don't transcribe, and
        // make sure a transcription already in flight doesn't insert text once it comes back.
        function forceCancel() {
            discarded = true;
            if (mediaRecorder && mediaRecorder.state !== 'inactive') {
                mediaRecorder._cancelled = true;
                mediaRecorder.stop();
            } else {
                reset();
            }
        }

        async function start() {
            try {
                stream = await navigator.mediaDevices.getUserMedia({audio: true});
            } catch (e) {
                alert('Microphone access denied.');
                return;
            }

            mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
            mediaRecorder = new MediaRecorder(stream, {mimeType});
            chunks = [];
            mediaRecorder.ondataavailable = e => {
                if (e.data.size > 0) chunks.push(e.data);
            };

            // Hide the field, overlay the voice bar in its place.
            $target = $(visibleTarget(el, fieldType));
            $target.css('visibility', 'hidden');
            $btn.css('display', 'none');

            const built = buildOverlay();
            $overlay = built.$overlay;
            const canvasEl = built.$canvas[0];
            positionOverlay($overlay, $target[0]);

            mediaRecorder.onstop = async () => {
                stopWaveform(canvasEl, waveState);
                if (mediaRecorder._cancelled) {
                    reset();
                    return;
                }
                $overlay.addClass('processing');
                stream.getTracks().forEach(tr => tr.stop());
                await transcribe(new Blob(chunks, {type: mimeType}), mimeType);
            };

            built.$cancel.on('click', e => {
                e.preventDefault();
                e.stopPropagation();
                mediaRecorder._cancelled = true;
                if (mediaRecorder.state !== 'inactive') mediaRecorder.stop();
                else reset();
            });
            built.$confirm.on('click', e => {
                e.preventDefault();
                e.stopPropagation();
                mediaRecorder._cancelled = false;
                if (mediaRecorder.state !== 'inactive') mediaRecorder.stop();
            });

            mediaRecorder.start(100);
            waveState.recording = true;
            activeRecordings.set(el, forceCancel);

            audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            const src = audioCtx.createMediaStreamSource(stream);
            analyser = audioCtx.createAnalyser();
            analyser.fftSize = 256;
            src.connect(analyser);

            startWaveform(canvasEl, analyser, waveState);
        }

        async function transcribe(blob, mimeType) {
            try {
                const audio = await blobToBase64(blob);
                const response = await fetch('/ai/api/transcribe', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken()},
                    body: JSON.stringify({
                        audio,
                        mimeType,
                        sessionId: 'field-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
                        tenantDb: getStoredTenantDb() || undefined,
                        userId: getCurrentUserId() || undefined
                    })
                });

                if (!response.ok) throw new Error('Transcription failed');

                const data = await response.json();
                if (data.text && !discarded) {
                    insertText(el, fieldType, data.text);
                }
            } catch (err) {
                if (!discarded) {
                    console.error('Speech-to-text error:', err);
                    alert('Transcription failed. Please try again.');
                }
            } finally {
                reset();
            }
        }

        $btn.on('mousedown', e => e.preventDefault());
        $btn.on('click', e => {
            e.preventDefault();
            e.stopPropagation();
            start();
        });
    }

    function scan(root) {
        const $root = $(root);
        $root.find(SELECTOR).each((i, el) => attachMic(el));
        if ($root.is(SELECTOR)) {
            attachMic($root[0]);
        }
    }

    // A field's popup can go away out from under an active recording without ever removing the
    // field from the DOM (a Bootstrap modal just hides), so on top of the disconnect check below
    // we also listen for modals closing and cancel any recording they contain.
    $(document).on('hide.bs.modal', e => {
        activeRecordings.forEach((cancel, el) => {
            if (e.target.contains(el)) {
                cancel();
            }
        });
    });

    function cancelDisconnectedRecordings() {
        if (!activeRecordings.size) return;
        activeRecordings.forEach((cancel, el) => {
            if (!document.body.contains(el)) {
                cancel();
            }
        });
    }

    $(function () {
        scan(document);

        if (typeof MutationObserver !== 'undefined') {
            const observer = new MutationObserver(mutations => {
                mutations.forEach(mutation => {
                    mutation.addedNodes.forEach(node => {
                        if (node.nodeType === 1) {
                            scan(node);
                        }
                    });
                });
                cancelDisconnectedRecordings();
            });
            observer.observe(document.body, {childList: true, subtree: true});
        }
    });

    // Exposed so views that build markup without triggering a mutation the observer catches
    // (rare) can re-scan a specific container on demand.
    window.attachSpeechToText = scan;
})();
